import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, XCircle, AlertCircle, Clock, RefreshCw, ExternalLink, Monitor, Tablet, Smartphone, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getBrowserLabel } from '@/lib/browserEnv';

/**
 * Admin-only QA dashboard.
 *
 * Surfaces the latest Playwright run, the release-gate checklist, and the
 * current visitor's environment. Run reports are uploaded as JSON to the
 * `qa-reports` storage bucket (or skipped — the page degrades gracefully).
 *
 * Report shape (see playwright.config.ts json reporter):
 *   { stats: { expected, unexpected, skipped, ... }, suites: [...] }
 */

interface QARunSummary {
  uploadedAt: string;
  commit?: string;
  version?: string;
  stats: {
    expected: number;
    unexpected: number;
    skipped: number;
    flaky?: number;
    duration?: number;
  };
  failures: Array<{ title: string; project?: string; error?: string }>;
}

const BUILD_VERSION =
  (import.meta as any).env?.VITE_BUILD_VERSION ||
  (import.meta as any).env?.VITE_COMMIT_SHA ||
  'dev';
const BUILD_TIME = (import.meta as any).env?.VITE_BUILD_TIME || new Date().toISOString();

const MATRIX = {
  desktop: ['chromium-desktop', 'firefox-desktop', 'webkit-desktop'],
  tablet: ['ipad-pro', 'galaxy-tab'],
  mobile: ['iphone-13', 'iphone-se', 'pixel-7', 'galaxy-s9'],
  inApp: ['facebook-inapp', 'instagram-inapp', 'tiktok-inapp', 'messenger-inapp'],
};

export default function AdminQA() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [run, setRun] = useState<QARunSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      if (!data) {
        navigate('/dashboard');
        return;
      }
      setIsAdmin(true);
    });
  }, [user, navigate]);

  const loadRun = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to fetch the latest run from the qa-reports bucket. This is
      // optional — if the bucket doesn't exist yet we just display N/A.
      const { data, error: listErr } = await supabase.storage
        .from('qa-reports')
        .list('', { limit: 1, sortBy: { column: 'created_at', order: 'desc' } });
      if (listErr) throw listErr;
      const latest = data?.[0];
      if (!latest) {
        setRun(null);
        return;
      }
      const { data: file, error: dlErr } = await supabase.storage
        .from('qa-reports')
        .download(latest.name);
      if (dlErr) throw dlErr;
      const json = JSON.parse(await file.text());
      setRun(normalizeRun(json, latest.created_at ?? new Date().toISOString()));
    } catch (e: any) {
      // Non-fatal — page should still render the manual checklist.
      setError(e?.message ?? 'Could not load QA report');
      setRun(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadRun();
  }, [isAdmin]);

  const status = useMemo<'pass' | 'fail' | 'unknown'>(() => {
    if (!run) return 'unknown';
    return run.stats.unexpected === 0 ? 'pass' : 'fail';
  }, [run]);

  if (isAdmin === null) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground">
        Checking access…
      </div>
    );
  }

  return (
    <main className="min-h-dvh bg-background text-foreground px-4 sm:px-6 py-10 max-w-6xl mx-auto">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
          Admin · Release Gate
        </p>
        <h1 className="text-3xl sm:text-4xl font-semibold">QA Dashboard</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Permanent QA process for Say It NOW. Automated coverage runs across desktop,
          tablet, mobile, and in-app browser environments. See{' '}
          <a className="underline" href="https://github.com/" target="_blank" rel="noreferrer">
            docs/QA_GATE.md
          </a>{' '}
          for the full release checklist.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-3 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Build version</CardDescription>
            <CardTitle className="text-lg font-mono break-all">{BUILD_VERSION}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Built {new Date(BUILD_TIME).toLocaleString()}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Last QA run</CardDescription>
            <CardTitle className="text-lg">
              {run ? new Date(run.uploadedAt).toLocaleString() : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            {run
              ? `${run.stats.expected} passed · ${run.stats.unexpected} failed · ${run.stats.skipped} skipped`
              : 'No automated report uploaded yet'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Release status</CardDescription>
            <CardTitle className="text-lg flex items-center gap-2">
              {status === 'pass' && (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <span className="text-emerald-400">Green</span>
                </>
              )}
              {status === 'fail' && (
                <>
                  <XCircle className="h-5 w-5 text-red-400" />
                  <span className="text-red-400">Failing</span>
                </>
              )}
              {status === 'unknown' && (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-400" />
                  <span className="text-amber-400">Unknown</span>
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button size="sm" variant="outline" onClick={loadRun} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardContent>
        </Card>
      </section>

      {error && (
        <Card className="mb-8 border-amber-500/40">
          <CardContent className="py-4 text-sm text-amber-200/90 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {error}. The Playwright run reporter uploads to the <code>qa-reports</code>{' '}
              storage bucket — see <code>docs/QA_GATE.md</code>.
            </span>
          </CardContent>
        </Card>
      )}

      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Failed checks
        </h2>
        {run && run.failures.length === 0 && (
          <Card>
            <CardContent className="py-6 text-sm text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> All automated checks passing.
            </CardContent>
          </Card>
        )}
        {run && run.failures.length > 0 && (
          <div className="space-y-2">
            {run.failures.slice(0, 25).map((f, i) => (
              <Card key={i} className="border-red-500/40">
                <CardContent className="py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-red-200">{f.title}</div>
                      {f.project && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {f.project}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {f.error && (
                    <pre className="mt-2 text-[11px] text-muted-foreground whitespace-pre-wrap break-words max-h-32 overflow-auto">
                      {f.error}
                    </pre>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {!run && (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground">
              No automated run available. Trigger one with{' '}
              <code className="text-foreground">bun run test:e2e</code> and upload{' '}
              <code className="text-foreground">playwright-report/results.json</code> to the{' '}
              <code className="text-foreground">qa-reports</code> bucket.
            </CardContent>
          </Card>
        )}
      </section>

      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3">Browser & device coverage</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MatrixCard icon={<Monitor className="h-4 w-4" />} title="Desktop" projects={MATRIX.desktop} run={run} />
          <MatrixCard icon={<Tablet className="h-4 w-4" />} title="Tablet" projects={MATRIX.tablet} run={run} />
          <MatrixCard icon={<Smartphone className="h-4 w-4" />} title="Mobile" projects={MATRIX.mobile} run={run} />
          <MatrixCard icon={<Globe className="h-4 w-4" />} title="In-app browsers" projects={MATRIX.inApp} run={run} />
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3">Browser compatibility notes</h2>
        <Card>
          <CardContent className="py-4 text-sm space-y-2 text-muted-foreground">
            <p>
              <strong className="text-foreground">In-app browsers</strong> (Facebook, Instagram, TikTok, Messenger)
              show a dismissible banner with "Open in Browser" via{' '}
              <code>intent://</code> (Android) and <code>x-safari-https://</code> (iOS).
            </p>
            <p>
              <strong className="text-foreground">Chunk recovery</strong> auto-reloads on stale Vite chunks once per
              session; second failure surfaces the branded fallback (never blank).
            </p>
            <p>
              <strong className="text-foreground">Auth hydration</strong> is capped at 4 s via Promise.race so a hung
              Supabase call cannot block render.
            </p>
            <p>
              <strong className="text-foreground">iOS Safari</strong> fullscreen video exit, native share sheet, and
              Samsung Internet quirks remain manual-only checks — see <code>docs/QA_GATE.md §4</code>.
            </p>
          </CardContent>
        </Card>
      </section>

      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3">Your current environment</h2>
        <Card>
          <CardContent className="py-4 text-sm grid gap-2 sm:grid-cols-2">
            <Row label="Browser label" value={getBrowserLabel()} />
            <Row label="User agent" value={typeof navigator !== 'undefined' ? navigator.userAgent : '—'} mono />
            <Row label="Viewport" value={`${window.innerWidth} × ${window.innerHeight}`} />
            <Row label="DPR" value={String(window.devicePixelRatio)} />
            <Row label="Language" value={typeof navigator !== 'undefined' ? navigator.language : '—'} />
            <Row label="Online" value={navigator.onLine ? 'yes' : 'no'} />
          </CardContent>
        </Card>
      </section>

      <Separator className="my-8" />

      <section className="text-sm text-muted-foreground">
        <p className="mb-2">Run the full matrix locally:</p>
        <pre className="bg-muted/40 rounded-md p-3 text-xs overflow-x-auto">
{`bunx playwright install --with-deps
bun run test:e2e
bun run test:e2e:report`}
        </pre>
        <p className="mt-3">
          Or against the live preview:
        </p>
        <pre className="bg-muted/40 rounded-md p-3 text-xs overflow-x-auto">
{`PLAYWRIGHT_NO_SERVER=1 \\
PLAYWRIGHT_BASE_URL=https://sayitnow.lovable.app \\
bun run test:e2e`}
        </pre>
        <a
          href="/docs/QA_GATE.md"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 mt-4 underline text-foreground"
        >
          Open full release checklist <ExternalLink className="h-3 w-3" />
        </a>
      </section>
    </main>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs break-all text-foreground' : 'text-foreground'}>
        {value}
      </span>
    </div>
  );
}

function MatrixCard({
  icon,
  title,
  projects,
  run,
}: {
  icon: React.ReactNode;
  title: string;
  projects: string[];
  run: QARunSummary | null;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {projects.map((p) => {
          const failed = run?.failures.some((f) => f.project === p) ?? false;
          const status = !run ? 'unknown' : failed ? 'fail' : 'pass';
          return (
            <div key={p} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{p}</span>
              {status === 'pass' && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">PASS</Badge>}
              {status === 'fail' && <Badge className="bg-red-500/15 text-red-300 border-red-500/30">FAIL</Badge>}
              {status === 'unknown' && <Badge variant="outline" className="text-muted-foreground">—</Badge>}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function normalizeRun(raw: any, uploadedAt: string): QARunSummary {
  const stats = raw?.stats ?? {};
  const failures: QARunSummary['failures'] = [];
  const walkSuites = (suites: any[]) => {
    for (const s of suites ?? []) {
      for (const spec of s.specs ?? []) {
        for (const t of spec.tests ?? []) {
          const ok = (t.results ?? []).every((r: any) => r.status === 'passed' || r.status === 'skipped');
          if (!ok) {
            const errMsg = (t.results ?? [])
              .flatMap((r: any) => (r.errors ?? []).map((e: any) => e.message))
              .filter(Boolean)
              .join('\n');
            failures.push({
              title: spec.title || t.title || 'unnamed test',
              project: t.projectName || t.projectId,
              error: errMsg,
            });
          }
        }
      }
      if (s.suites) walkSuites(s.suites);
    }
  };
  walkSuites(raw?.suites ?? []);
  return {
    uploadedAt,
    commit: raw?.commit,
    version: raw?.version,
    stats: {
      expected: stats.expected ?? 0,
      unexpected: stats.unexpected ?? failures.length,
      skipped: stats.skipped ?? 0,
      flaky: stats.flaky ?? 0,
      duration: stats.duration,
    },
    failures,
  };
}
